<template>
    <v-form ref="form" autocomplete="off" @submit.prevent="save" >
        <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                                
                                prepend-icon="person"
                                name="username"
                                v-model="form.username"
                                :label="$t('admin.inviraUser.labels.username')"
                                :placeholder="$t('admin.inviraUser.labels.username')"
                                :error="hasInputErrors('username')"
                                :error-messages="getInputErrors('username')"
                                color="secondary"
                                :rules="required"
                        ></v-text-field>
                    </v-col>
                    
                    <v-col cols="12" sm="6">
                        <v-text-field
                                
                                prepend-icon="email"
                                name="email"
                                v-model="form.email"
                                :label="$t('admin.inviraUser.labels.email')"
                                :placeholder="$t('admin.inviraUser.labels.email')"
                                :error="hasInputErrors('email')"
                                :error-messages="getInputErrors('email')"
                                color="secondary"
                                :rules="required"
                        ></v-text-field>
                    </v-col>
                    
                    <v-col cols="12" sm="6">
                        <v-text-field
                                
                                prepend-icon="lock"
                                name="password"
                                type="password"
                                v-model="form.password"
                                :label="$t('admin.inviraUser.labels.password')"
                                :placeholder="$t('admin.inviraUser.labels.password')"
                                :error="hasInputErrors('password')"
                                :error-messages="getInputErrors('password')"
                                color="secondary"
                                :rules="passwordRequired ? required : []"
                        ></v-text-field>
                    </v-col>
                    
                    <v-col cols="12" sm="6">
                        <v-select
                                
                                prepend-icon="business"
                                v-model="form.companyId"
                                :items="companies"
                                item-text="razonSocial"
                                item-value="id"
                                :label="$t('admin.inviraUser.labels.company')"
                                :error="hasInputErrors('companyId')"
                                :error-messages="getInputErrors('companyId')"
                                color="secondary"
                                :rules="required"
                        ></v-select>
                    </v-col>
                    
                    <v-col cols="12">
                        <v-select
                                prepend-icon="verified_user"
                                v-model="form.roleId"
                                :items="assignableRoles"
                                item-text="name"
                                item-value="id"
                                :label="$t('admin.inviraUser.labels.role')"
                                :error="hasInputErrors('roleId')"
                                :error-messages="getInputErrors('roleId')"
                                color="secondary"
                        ></v-select>
                    </v-col>
                    
                    <v-col cols="12">
                        <v-select
                                prepend-icon="warehouse"
                                v-model="form.depositoIds"
                                :items="depositos"
                                item-text="nombre"
                                item-value="id"
                                :label="$t('admin.inviraUser.labels.depositos')"
                                :error="hasInputErrors('depositoIds')"
                                :error-messages="getInputErrors('depositoIds')"
                                color="secondary"
                                multiple
                                chips
                                :loading="loadingDepositos"
                                :disabled="!form.companyId"
                        ></v-select>
                    </v-col>
                    
                    <v-col cols="12" sm="6">
                        <v-switch
                                v-model="form.active"
                                :label="$t('admin.inviraUser.labels.active')"
                                color="secondary"
                        ></v-switch>
                    </v-col>
        </v-row>
    </v-form>
</template>

<script>

    import {InputErrorsByProps, RequiredRule } from '@dracul/common-frontend'
    import CompanyProvider from "../../../providers/CompanyProvider";
    import InviraRoleProvider from "../../../providers/InviraRoleProvider";
    import DepositoProvider from "../../../providers/DepositoProvider";
    
    export default {
        name: "InviraUserForm",
        mixins: [InputErrorsByProps, RequiredRule],
        
        props:{
            value: {
                type: Object,
                required: true
            },
            passwordRequired: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                companies: [],
                roles: [],
                depositos: [],
                loadingDepositos: false
            }
        },
        computed: {
           form: {
                get() { return this.value },
                set(val) {this.$emit('input', val)}
            },
            // Roles that can be assigned manually (admin is excluded)
            assignableRoles() {
                return (this.roles || []).filter(
                    r => (r.name || '').toLowerCase() !== 'admin'
                )
            }
        },
        created() {
            this.loadCompanies()
            // Roles and depositos are loaded only by the form.companyId watcher (immediate: true) to avoid duplicate adminInviraRoleFetch and adminDepositoFetch
        },
        watch: {
            form: {
                handler(newVal) {
                    this.$emit('input', newVal)
                },
                deep: true
            },
            'form.companyId': {
                handler(newCompanyId, oldCompanyId) {
                    // Only reload if company actually changed
                    if (newCompanyId !== oldCompanyId) {
                        // Reload roles and deposits when company changes
                        if (newCompanyId) {
                            this.loadRoles(newCompanyId)
                            this.loadDepositos(newCompanyId)
                            // Clear selected role and deposits only if company was changed (not on initial load)
                            if (oldCompanyId !== undefined) {
                                this.$nextTick(() => {
                                    this.$emit('input', { ...this.form, roleId: null, depositoIds: [] })
                                })
                            }
                        } else {
                            this.roles = []
                            this.depositos = []
                        }
                    }
                },
                immediate: true
            },
            roles: {
                handler(newRoles) {
                    // When roles are loaded, ensure roleId is preserved and in correct format
                    if (newRoles && newRoles.length > 0 && this.form.roleId) {
                        // Normalize all role IDs to strings
                        const roleIdString = String(this.form.roleId).trim()
                        const availableIds = new Set(newRoles.map(r => String(r.id).trim()))
                        
                        // Check if the roleId exists in the loaded roles
                        if (availableIds.has(roleIdString)) {
                            // Re-set roleId to trigger reactivity
                            this.$nextTick(() => {
                                this.$emit('input', { ...this.form, roleId: roleIdString })
                            })
                        }
                    }
                },
                immediate: false
            },
            depositos: {
                handler(newDepositos, oldDepositos) {
                    // Only process when depositos are actually loaded (not when user changes selection)
                    // Check if this is a new load by comparing array length or if oldDepositos was empty
                    const isNewLoad = !oldDepositos || oldDepositos.length === 0 || 
                                     (newDepositos && newDepositos.length > 0 && 
                                      (!oldDepositos || oldDepositos.length !== newDepositos.length))
                    
                    if (isNewLoad && newDepositos && newDepositos.length > 0 && this.form.depositoIds && this.form.depositoIds.length > 0) {
                        // Normalize all IDs to strings and trim whitespace
                        const depositoIdsAsStrings = this.form.depositoIds.map(id => String(id).trim())
                        const availableIds = new Set(newDepositos.map(d => String(d.id).trim()))
                        
                        // Filter to only include IDs that exist in the loaded depositos
                        const validDepositoIds = depositoIdsAsStrings.filter(id => {
                            const normalizedId = String(id).trim()
                            return availableIds.has(normalizedId)
                        })
                        
                        // Only update if there are valid IDs and they differ from current
                        if (validDepositoIds.length > 0) {
                            const currentIds = (this.form.depositoIds || []).map(id => String(id).trim()).sort().join(',')
                            const newIds = validDepositoIds.sort().join(',')
                            
                            if (currentIds !== newIds) {
                                this.$nextTick(() => {
                                    this.$emit('input', { ...this.form, depositoIds: validDepositoIds })
                                })
                            }
                        }
                    }
                },
                immediate: false
            }
        },
        methods: {
            loadCompanies() {
                CompanyProvider.fetchCompanies()
                    .then(r => {
                        this.companies = r.data.companyFetch || []
                    })
                    .catch(err => console.error(err))
            },
            loadRoles(companyId) {
                if (!companyId) {
                    this.roles = []
                    return
                }
                InviraRoleProvider.fetchRoles(companyId)
                    .then(r => {
                        const loadedRoles = r.data.adminInviraRoleFetch || []
                        // Normalize all role IDs to strings for consistent comparison
                        const normalizedRoles = loadedRoles.map(role => ({
                            ...role,
                            id: String(role.id).trim()
                        }))
                        this.roles = normalizedRoles
                    })
                    .catch(err => {
                        console.error('Error loading roles:', err)
                        this.roles = []
                    })
            },
            loadDepositos(companyId) {
                if (!companyId) {
                    this.depositos = []
                    return
                }
                this.loadingDepositos = true
                // Store current depositoIds before loading to preserve them - normalize to strings
                const currentDepositoIds = (this.form.depositoIds || []).map(id => String(id).trim())
                
                DepositoProvider.fetchDepositos(companyId)
                    .then(r => {
                        const loadedDepositos = r.data.adminDepositoFetch || []
                        // Normalize all deposito IDs to strings for consistent comparison
                        const normalizedDepositos = loadedDepositos.map(dep => ({
                            ...dep,
                            id: String(dep.id).trim()
                        }))
                        this.depositos = normalizedDepositos
                        
                        // After loading, ensure depositoIds are preserved and match loaded depositos
                        if (currentDepositoIds.length > 0 && normalizedDepositos.length > 0) {
                            // Create a Set of available deposito IDs for faster lookup
                            const availableIds = new Set(normalizedDepositos.map(dep => dep.id))
                            
                            // Filter to only include IDs that exist in the loaded depositos
                            const validDepositoIds = currentDepositoIds.filter(id => {
                                const normalizedId = String(id).trim()
                                return availableIds.has(normalizedId)
                            })
                            
                            // Always update form with valid IDs (even if empty, to trigger reactivity)
                            if (validDepositoIds.length > 0) {
                                this.$nextTick(() => {
                                    this.$emit('input', { ...this.form, depositoIds: validDepositoIds })
                                })
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Error loading depositos:', err)
                        this.depositos = []
                    })
                    .finally(() => {
                        this.loadingDepositos = false
                    })
            },
            validate(){
              return this.$refs.form.validate()
            }
        }
    }
</script>

<style scoped>

</style>

